/**
 * AnnotatedText Component
 * Renders text with precise CEFR level highlighting based on position annotations.
 * Uses exact character positions to highlight words, supporting lemmatized matches.
 */

import React, { useState, useMemo } from 'react';
import { DictionaryPopover } from './DictionaryPopover';
import { ClickableWord } from './ClickableWord';
import { splitPossessive } from '../utils/wordSplit';
import type { WordAnnotation, CEFRLevel } from '../services/cefrKnowledgeService';
import './AnnotatedText.css';

interface AnnotatedTextProps {
  text: string;
  annotations: WordAnnotation[];
  className?: string;
}

interface TextSegment {
  text: string;
  isAnnotated: boolean;
  annotation?: WordAnnotation;
  startPos: number;
  endPos: number;
}

export const AnnotatedText: React.FC<AnnotatedTextProps> = ({
  text,
  annotations,
  className = '',
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverWord, setPopoverWord] = useState<string>('');
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

  // Build segments from text and annotations
  const segments = useMemo(() => {
    if (!annotations || annotations.length === 0) {
      return [{ text, isAnnotated: false, startPos: 0, endPos: text.length }];
    }

    // Sort annotations by start position
    const sortedAnnotations = [...annotations].sort(
      (a, b) => a.startPosition - b.startPosition
    );

    const result: TextSegment[] = [];
    let currentPos = 0;

    for (const ann of sortedAnnotations) {
      // Add non-annotated text before this annotation
      if (ann.startPosition > currentPos) {
        result.push({
          text: text.slice(currentPos, ann.startPosition),
          isAnnotated: false,
          startPos: currentPos,
          endPos: ann.startPosition,
        });
      }

      // Add the annotated segment
      result.push({
        text: text.slice(ann.startPosition, ann.endPosition),
        isAnnotated: true,
        annotation: ann,
        startPos: ann.startPosition,
        endPos: ann.endPosition,
      });

      currentPos = ann.endPosition;
    }

    // Add remaining text after last annotation
    if (currentPos < text.length) {
      result.push({
        text: text.slice(currentPos),
        isAnnotated: false,
        startPos: currentPos,
        endPos: text.length,
      });
    }

    return result;
  }, [text, annotations]);

  const handleWordClick = (
    e: React.MouseEvent<HTMLSpanElement>,
    word: string
  ) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = rect.left;
    let y = rect.bottom + 8;

    if (x + 400 > viewportWidth) {
      x = viewportWidth - 420;
    }

    if (y + 300 > viewportHeight) {
      y = rect.top - 308;
    }

    setPopoverPosition({ x, y });
    setPopoverWord(word);
    setShowPopover(true);
  };

  const getCefrClass = (level: CEFRLevel): string => {
    return `annotated-word--cefr-${level}`;
  };

  const renderClickableSegment = (segmentText: string, startPos: number): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    const wordRegex = /\b[a-zA-Z]+(?:['-][a-zA-Z]+)*\b/g;
    let lastIndex = 0;
    let match;

    while ((match = wordRegex.exec(segmentText)) !== null) {
      if (match.index > lastIndex) {
        parts.push(segmentText.substring(lastIndex, match.index));
      }
      // Split off possessive "'s" so the base noun is what gets looked up.
      const { base, tail } = splitPossessive(match[0]);
      parts.push(
        <ClickableWord
          key={`unannotated-${startPos + match.index}`}
          word={base}
        />
      );
      if (tail) {
        parts.push(
          <span key={`unannotated-tail-${startPos + match.index}`} className="clickable-word__possessive">
            {tail}
          </span>
        );
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < segmentText.length) {
      parts.push(segmentText.substring(lastIndex));
    }

    return parts;
  };

  return (
    <>
      <span className={`annotated-text ${className}`}>
        {segments.map((segment, idx) => {
          if (!segment.isAnnotated) {
            return <span key={idx}>{renderClickableSegment(segment.text, segment.startPos)}</span>;
          }

          const ann = segment.annotation;
          if (!ann) return <span key={idx}>{segment.text}</span>;
          return (
            <span
              key={idx}
              className={`annotated-word ${getCefrClass(ann.cefrLevel)} ${
                ann.matchType === 'lemma' ? 'annotated-word--lemma' : ''
              }`}
              onClick={(e) => handleWordClick(e, segment.text)}
              title={`${ann.cefrLevel} - ${ann.matchType === 'lemma' ? `(${ann.baseForm})` : ''} Click for definition`}
              data-cefr={ann.cefrLevel}
              data-base-form={ann.baseForm}
            >
              {segment.text}
              <span className="annotated-word__level">{ann.cefrLevel}</span>
            </span>
          );
        })}
      </span>

      {showPopover && (
        <DictionaryPopover
          word={popoverWord}
          position={popoverPosition}
          onClose={() => setShowPopover(false)}
        />
      )}
    </>
  );
};

export default AnnotatedText;
